import { getTheme, IconButton, IStyle, ITheme, mergeStyleSets, Modal } from "@fluentui/react";
import { ProductDetailPanelContent } from "../../../shop/components/product-detail-panel/content";
import { fontFamily } from "../../../../app-core/app-theme";
import { useCallback, useMemo, useState } from "react";
import { IBrowserImage } from "../../../../framework/components/bwj-image-browser";
import { distinct, forEach } from "../../../../framework/utils/collection";
import { IProductReview } from "../../../../entities-generated/common/product-review";
import { IProductTexture } from "../../../../entities-generated/common/product-texture";
import { BwjViewManager, BwjViewState } from "../../../../framework/components/bwj-view-manager";
import { BwjSemiFlexContainer } from "../../../../framework/components/bwj-semi-flex-container";
import { BwjScrollContainer } from "../../../../framework/components/bwj-scroll-container";
import { BwjConscientiousContainer } from "../../../../framework/components/bwj-conscientious-container";
import { noWrapStyle } from "../../../../framework/utils/no-wrap-style";
import { isTouchDevice } from "../../../../framework/utils/device";


interface IProductDetailDialogProps {
  id: string;
  brand: string;
  title: string;
  description: string;
  price: number;
  averageRating?: number | undefined;
  rating?: IProductReview | undefined;
  keyFeatures: string[];
  includedServices?: string[] | undefined;
  variant: IProductTexture;
  variantDescriptor: string;
  priceChange?: number | undefined;
  onChangeSelection: () => void;
  imageUrls: string[];
  showDialog: boolean;
  isLoading: boolean;
  onCloseDialog: () => void;
  onSeeInPersonRequested: (() => void) | undefined;
}

const getStyleClasses = (theme: ITheme) => {

  const main: IStyle = {
    width: 'calc(100% - 3rem)',
    maxWidth: '75rem',
    height: 'calc(100vh - 10rem)',
  };
  const msScrollableContent: IStyle = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  };
  const root: IStyle = {
    fontFamily: fontFamily,

    '& .ms-Dialog-main': main,
    '& .ms-Modal-scrollableContent': msScrollableContent,
  };

  const header: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: theme.fonts.xSmall.fontSize,
    alignItems: 'center',

    '& > h1': {
      ...noWrapStyle as any,
      fontSize: theme.fonts.xLarge.fontSize,
      lineHeight: theme.fonts.xLarge.lineHeight,
      fontWeight: 500,
      margin: 0,
      padding: 0,
      width: 1,
      flexGrow: 1,
    }
  };

  const scrollBody: IStyle = {
    padding: '0 1rem',
    paddingTop: '1rem',
    paddingBottom: 96,
  };

  const contentContainer: IStyle = {
    maxWidth: 1140,
    minWidth: 300,
    margin: '0 auto',
  };

  return mergeStyleSets({
    root,
    header,
    scrollBody,
    contentContainer,
  });
}

const getCloseButtonStyles = (theme: ITheme) => {
  return {
    root: {
      color: theme.palette.neutralPrimary,
      marginLeft: 'auto',
      marginTop: '4px',
      marginRight: '2px',
    },
    rootHovered: {
      color: theme.palette.neutralDark,
    },
  };
}

const touchDevice = isTouchDevice();

export const ProductDetailDialog: React.FunctionComponent<IProductDetailDialogProps> = (props) => {
  const theme = getTheme();
  const cs = getStyleClasses(theme);
  const closeButtonStyles = getCloseButtonStyles(theme);

  const { variant, imageUrls, description } = props;
  const [contentWidth, setContentWidth] = useState(0);
  const [displayedImageIndex, setDisplayedImageIndex] = useState(0);
  const viewState = props.isLoading ? BwjViewState.Loading : BwjViewState.Ready;
  const images = useMemo(() => {
    const imgs: IBrowserImage[] = variant.productImageUrls.map(src => {
      return { src, alt: description };
    });

    forEach(imageUrls, src => {
      imgs.push({ src, alt: description });
    });

    const uniqueImgs = distinct(imgs, i => i.src);
    return uniqueImgs;
  }, [imageUrls, variant, description]);

  const onWidthChange = useCallback((width: number) => setContentWidth(width), []);

  return (
    <Modal
      isOpen={props.showDialog}
      isBlocking={true}
      onDismiss={props.onCloseDialog}
      className={cs.root}
      allowTouchBodyScroll={touchDevice}
    >
      <div className={cs.header}>
        <h1>{props.title}</h1>
        {props.isLoading === false &&<IconButton
          styles={closeButtonStyles}
          iconProps={{ iconName: 'Cancel' }}
          ariaLabel='Close product detail dialog'
          onClick={props.onCloseDialog}
        />}
      </div>
      <BwjSemiFlexContainer
        pinHeight={true}
        styles={{
          root: { height: 1, flexGrow: 1, width: '100%' },
          content: {},
        }}
      >
        <BwjViewManager
          state={viewState}
          loadingState={{
            message: 'Getting details...',
          }}
        >
          <BwjScrollContainer>
            <div className={cs.scrollBody}>
              <BwjConscientiousContainer
                className={cs.contentContainer}
                pollingFrequency={1200}
                onWidthChange={onWidthChange}
              >
                <ProductDetailPanelContent
                  id={props.id}
                  brand={props.brand}
                  title={description}
                  price={props.price}
                  averageRating={props.averageRating}
                  rating={props.rating}
                  keyFeatures={props.keyFeatures}
                  includedServices={props.includedServices}
                  imageUrls={images}
                  displayedImageIndex={displayedImageIndex}
                  onDisplayedIndexChanged={setDisplayedImageIndex}
                  variants={[]}
                  variantDescriptor={props.variantDescriptor}
                  priceChange={props.priceChange}
                  chooseProductButtonText={'Change selection'}
                  onProductChosen={props.onChangeSelection}
                  chooseVariantButtonText={''}
                  selectedVariant={variant}
                  onVariantSelected={() => {}}
                  onVariableOptionsVisibilityChange={() => {}}
                  onSeeInPersonRequested={props.onSeeInPersonRequested}
                  verticalScrollInfo={{ scrollY: 0, vpHeight: 0, containerSelector: ''}}
                  contentWidth={contentWidth}
                />
              </BwjConscientiousContainer>
            </div>
          </BwjScrollContainer>
        </BwjViewManager>
      </BwjSemiFlexContainer>
    </Modal>
  );
};
